import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Site, SiteRates, SiteTariffs, UtilityTariffs } from '../../../../domain/cloud-topology';
import { Subject } from 'rxjs';
import { SiteService } from '../../../../services/site/site.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-tariffs',
  templateUrl: './tariffs.component.html',
  styleUrls: ['./tariffs.component.scss'],
  standalone: false,
})
export class TariffsComponent implements OnDestroy, OnChanges {
  @Input() site: Site;
  @Input() utility: string;
  @Input() siteTariffs: SiteTariffs;
  @Input() isDisabled: boolean;
  @Output() isTariffFormDirty: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() siteTariffsChange = new EventEmitter<SiteTariffs>();
  destroy$ = new Subject<void>();
  rates: SiteRates;
  tariffsForm: FormGroup;
  utilityTariffs: UtilityTariffs;

  constructor(private siteService: SiteService,
              private snackBar: MatSnackBar) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.siteTariffs) {
      this.tariffsForm = new FormGroup({
        supplyBefore: new FormControl(null),
        deliveryBefore: new FormControl(null, Validators.required),
        supplyAfter: new FormControl(null),
        deliveryAfter: new FormControl(null, [Validators.required]),
        supplyCharge: new FormControl(null),
      });
    }

    if (changes['utility'] && (changes['utility']['currentValue'] != changes['utility']['previousValue']) && this.tariffsForm) {
      this.tariffsForm.reset({
        supplyBefore: null,
        deliveryBefore: null,
        supplyAfter: null,
        deliveryAfter: null,
        supplyCharge: null,
      });
    }

    if (this.site && this.utility && this.siteTariffs) {
      this.siteService.getRates(this.site.id).pipe(takeUntil(this.destroy$)).subscribe(rates => {
        this.rates = rates;
        this.setTariffs();
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setTariffs(): void {
    this.siteService.getUtilityTariffs(this.utility).subscribe(utilityTariffs => {
      this.utilityTariffs = utilityTariffs;

      this.checkForOrphanSiteTariffs();
      this.tariffsForm = new FormGroup({
        supplyBefore: new FormControl(this.siteTariffs.supplyBefore),
        deliveryBefore: new FormControl(this.siteTariffs.deliveryBefore, Validators.required),
        supplyAfter: new FormControl(this.siteTariffs.supplyAfter),
        deliveryAfter: new FormControl(this.siteTariffs.deliveryAfter, [Validators.required]),
        supplyCharge: new FormControl(this.siteTariffs.supplyCharge),
      });
    }, () => {
      console.warn('Tariff\'s are not set because utility is not assigned');
    });
  }

  private checkForOrphanSiteTariffs(): void {
    if (this.utilityTariffs) {
      if (this.siteTariffs.supplyBefore && !this.isTariffContained(this.siteTariffs.supplyBefore)) {
        this.siteTariffs.supplyBefore = null;
        this.displayOrphanTariffMessage('Supply Before');
      }

      if (this.siteTariffs.supplyAfter && !this.isTariffContained(this.siteTariffs.supplyAfter)) {
        this.siteTariffs.supplyAfter = null;
        this.displayOrphanTariffMessage('Supply After');
      }

      if (this.siteTariffs.deliveryBefore && !this.isTariffContained(this.siteTariffs.deliveryBefore)) {
        this.siteTariffs.deliveryBefore = null;
        this.displayOrphanTariffMessage('Delivery Before');
      }

      if (this.siteTariffs.deliveryAfter && !this.isTariffContained(this.siteTariffs.deliveryAfter)) {
        this.siteTariffs.deliveryAfter = null;
        this.displayOrphanTariffMessage('Delivery After');
      }
    }
  }

  private isTariffContained(tariffCode: string): boolean {
    for (const supplyTariff of this.utilityTariffs.supply) {
      if (tariffCode === supplyTariff.code) {
        return true;
      }
    }

    for (const deliveryTariff of this.utilityTariffs.delivery) {
      if (tariffCode === deliveryTariff.code) {
        return true;
      }
    }
    this.isTariffFormDirty.emit(true);
    return false;
  }

  private displayOrphanTariffMessage(fieldName: string): void {
    this.snackBar.open(`ATTENTION: The ${fieldName} tariff for ${this.site.name} is no longer valid. Please make another selection.`, 'Close', {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
    });
  }


  setDirtyForm() {
    this.isTariffFormDirty.emit(true);
  }

  deliveryTariffsSet() {
    return (this.tariffsForm.get('deliveryAfter').value != null && this.tariffsForm.get('deliveryBefore').value != null);
  }

  hasCustomSupplyRateSet(): boolean {
    const tariffs = [
      this.tariffsForm.get('supplyBefore').value,
      this.tariffsForm.get('supplyAfter').value,
    ];

    return tariffs.join(',').includes('Flat');
  }

  save() {
    if (window.confirm(`Are you sure you want to update tariffs for ${this.site.name}?`) && this.tariffsForm.valid) {

      if (!this.hasCustomSupplyRateSet()) {
        this.tariffsForm.get('supplyCharge').setValue(null);
      }

      this.siteService.putSiteTariffs(this.site.id, this.tariffsForm.value).subscribe(response => {
        this.siteTariffs = response;
        this.siteTariffsChange.emit(this.tariffsForm.value);
        this.isTariffFormDirty.emit(false);
        this.snackBar.open(`${this.site.name} tariff\'s have been updated.`, 'Close', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
        });
      }, () => {
        this.snackBar.open(`Error saving tariffs for site.`, 'Close', {
          duration: 3000,
          horizontalPosition: 'right',
          verticalPosition: 'bottom',
        });
      });
    }
  }

  cancel() {
    if (window.confirm(`Are you sure you want to discard tariff changes?`)) {
      this.isTariffFormDirty.emit(false);
      this.tariffsForm.reset({
        supplyBefore: this.siteTariffs.supplyBefore,
        deliveryBefore: this.siteTariffs.deliveryBefore,
        supplyAfter: this.siteTariffs.supplyAfter,
        deliveryAfter: this.siteTariffs.deliveryAfter,
        supplyCharge: this.siteTariffs.supplyCharge,
      });
    }
  }
}
